import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { LocalDate } from '../../../core/model/local-date';
import { PagedFilter } from '../../../core/model/paged-filter';
import { DataService } from '../../../core/service/base/data.service';
import { cleanV2FilterPayload } from '../../../core/service/base/filter-cleaner';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { InvoiceDeliveryMethod } from '../../model/const/enum';
import { InvoiceCategory } from '../../model/invoice';
import { InvoiceListItem } from '../../model/invoice-list-item';
import { InvoicePeriodItem } from '../../model/invoice-period';
import { withInvoiceDrilldownFlags } from '../../helper/invoice-drilldown-filter';

export interface InvoiceListFilterDefinition {
  company: string[];
  periodFrom: string;
  periodTo: string;
  invoicePeriods: InvoicePeriodItem[];
  selectedFields: string[];
  category: number;
  invDeliveryMethod: number;
  emlDeliveryStatusKind: number;
  invoiceNumber: string;
  invoiceDate: LocalDate;
  dueDate: LocalDate;
  ocr: string;
  recipientContactId: string;
  protectedIdentityStatus: boolean | null;
  property: string;
  directDebit: number;
  vatInvoice: number;
  sentToDebtCollection: boolean | null;
  invPeriodMonths: number;
  invoiceDeliveryMethods: InvoiceDeliveryMethod[];
  labeling: string;
  rentalId: string;
  invDeliveryMethodExecuted: number;
  recipientCategory: number;
  invoiceType: number;
  articles: string[];
  defaultFields: string[];
  companyCustomPropertiesTextValues: { key: string; value: string }[];
  companyCustomPropertiesDateValues: { key: string; value: LocalDate }[];
  companyCustomPropertiesNumericValues: { key: string; value: string }[];
  companyCustomPropertiesBooleanValues: { key: string; value: boolean }[];
}

/**
 * Data service for the V2 invoice list endpoint (`api/v2/invoice-list`).
 *
 * Routed through {@link DataServiceBase} which picks the v2 API host based
 * on the `ProvidedEndpoints` registered server-side.
 */
@Injectable()
export class InvoiceListService extends DataService<InvoiceListItem> {
  private readonly _defaultFilterFields: string[] = [
    'category',
    'company',
    'periodFrom',
    'periodTo',
    'invDeliveryMethod',
    'emlDeliveryStatusKind',
    'invoiceNumber',
    'recipientContactId',
    'property',
    'directDebit',
    'vatInvoice',
    'sentToDebtCollection',
    'invPeriodMonths',
    'invoiceType',
  ];

  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messageService: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, messageService, InvoiceListItem, progressBar, 'invoice-list');
  }

  getFilterDefinition(): Observable<InvoiceListFilterDefinition> {
    return this.client.get<PagedFilter<any>>(`${this.endpoint}/filter`).pipe(
      map(res => {
        const filter = res?.filter ?? {};
        const selectedFields = Array.isArray(filter.selectedFields)
          ? filter.selectedFields.map(field => field === 'recipientName' ? 'recipientContactId' : field)
          : this._defaultFilterFields;

        return {
          company: Array.isArray(filter.company) ? filter.company : filter.company ? [filter.company] : [],
          periodFrom: filter.periodFrom ? filter.periodFrom : '',
          periodTo: filter.periodTo ? filter.periodTo : '',
          invoicePeriods: Array.isArray(filter.periods) ? filter.periods.map(x => Object.assign(new InvoicePeriodItem(), x)) : [],
          category: Number.isInteger(filter.category) ? filter.category : InvoiceCategory.Any,
          invDeliveryMethod: filter.invDeliveryMethod ? filter.invDeliveryMethod : 0,
          emlDeliveryStatusKind: filter.emlDeliveryStatusKind ? filter.emlDeliveryStatusKind : 0,
          invoiceNumber: filter.invoiceNumber ? filter.invoiceNumber : '',
          invoiceDate: filter.invoiceDate ? filter.invoiceDate : null,
          dueDate: filter.dueDate ? filter.dueDate : null,
          ocr: filter.ocr ? filter.ocr : '',
          recipientContactId: filter.recipientContactId ? filter.recipientContactId : '',
          protectedIdentityStatus:
            typeof filter.protectedIdentityStatus === 'boolean' ? filter.protectedIdentityStatus : null,
          property: filter.property ? filter.property : '',
          directDebit: Number.isInteger(filter.directDebit) ? filter.directDebit : 0,
          vatInvoice: Number.isInteger(filter.vatInvoice) ? filter.vatInvoice : 0,
          sentToDebtCollection: typeof filter.sentToDebtCollection === 'boolean' ? filter.sentToDebtCollection : null,
          invPeriodMonths: filter.invPeriodMonths ? filter.invPeriodMonths : 0,
          invoiceDeliveryMethods: filter.invoiceDeliveryMethods ?? [],
          labeling: filter.labeling ? filter.labeling : '',
          rentalId: filter.rentalId ? filter.rentalId : '',
          invDeliveryMethodExecuted: filter.invDeliveryMethodExecuted ? filter.invDeliveryMethodExecuted : 0,
          recipientCategory: filter.recipientCategory ? filter.recipientCategory : null,
          invoiceType: filter.invoiceType ? filter.invoiceType : null,
          articles: filter.articles ?? [],
          selectedFields: selectedFields,
          defaultFields: this._defaultFilterFields,
          companyCustomPropertiesTextValues: Array.isArray(filter.companyCustomPropertiesTextValues)
            ? filter.companyCustomPropertiesTextValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesNumericValues: Array.isArray(filter.companyCustomPropertiesNumericValues)
            ? filter.companyCustomPropertiesNumericValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesDateValues: Array.isArray(filter.companyCustomPropertiesDateValues)
            ? filter.companyCustomPropertiesDateValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
          companyCustomPropertiesBooleanValues: Array.isArray(filter.companyCustomPropertiesBooleanValues)
            ? filter.companyCustomPropertiesBooleanValues.map(v => ({
                key: v.key,
                value: v.value,
              }))
            : [],
        };
      }),
      catchError(error => {
        console.log(error);
        return [];
      })
    );
  }

  /**
   * Sanitize the legacy filter form payload before sending it to the V2 endpoint.
   *
   * The shared filter UI populates every form control eagerly, so empty inputs are
   * posted as `""`, including cleared remote autocomplete values.
   * V1 ran on Newtonsoft which silently coerced these to `null` / dropped the empty
   * entries; V2 uses System.Text.Json which rejects them and `[ApiController]` turns
   * the binding error into a silent 400 BadRequest. We normalize the payload here
   * so the V2 model receives a contract-correct shape:
   *  - `""` (and whitespace-only strings) on scalar fields become `null`
   *  - GUID-backed autocomplete fields only post selected IDs, never their typed display text
   *  - empty/whitespace string entries are removed from array fields
   *
   * `selectedFields` is unaffected because it never contains empty entries.
   */
  override prepareFilter(filter: any): any {
    filter = cleanV2FilterPayload(super.prepareFilter(filter)); 

    filter.company = toGuidArray(filter.company);
    filter.articles = toGuidArray(filter.articles);
    filter.property = toNullableGuid(filter.property);
    filter.recipientContactId = toNullableGuid(filter.recipientContactId);
    filter.rentInvoicingOrderId = toNullableGuid(filter.rentInvoicingOrderId);

    filter = withInvoiceDrilldownFlags(filter, filter.ignoreSaveOnLoad === true, filter.effectiveRecipientOnly === true);

    return filter;
  }
}

const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function toGuidArray(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.filter((item): item is string => typeof item === 'string' && guidPattern.test(item));
}

function toNullableGuid(value: unknown): string | null {
  return typeof value === 'string' && guidPattern.test(value) ? value : null;
}
